import type { EmbeddedImageData } from '../features/ttqib/embeddedImageUtils';
import { readEmbeddedImageRecord } from '../features/ttqib/embeddedImageUtils';
import { assetService } from '../services/assetService';
import { kpWorkspaceService } from '../services/kpWorkspaceService';
import type { KnowledgeParticle } from '../types/domain';

export type ImageCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ParticleEmbedSource = {
  sourceAssetId: string;
  sourceImageUrl: string;
  ossId?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function readParticleSourceAssetId(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata) return '';
  const embedded = asRecord(metadata.relatedGraphEmbeddedImage) || asRecord(metadata.embeddedImage);
  const extraction = asRecord(metadata.knowledgeParticleExtraction)
    || asRecord(metadata.knowledge_particle_extraction)
    || asRecord(metadata.particleExtraction);
  const candidates: unknown[] = [
    embedded?.sourceAssetId,
    metadata.sourceAssetId,
    metadata.assetId,
    extraction?.assetId,
    extraction?.sourceAssetId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return '';
}

export function patchParticleEmbeddedImageMetadata(
  metadata: Record<string, unknown> | null | undefined,
  embeddedImage: {
    sourceAssetId?: string;
    sourceImageUrl: string;
    cropRect: ImageCropRect;
    imageWidth: number;
    imageHeight: number;
  },
): Record<string, unknown> {
  const { cropRect, imageWidth, imageHeight, sourceAssetId, sourceImageUrl } = embeddedImage;
  return {
    ...(metadata || {}),
    relatedGraphEmbeddedImage: {
      sourceAssetId,
      sourceImageUrl,
      cropRect,
      cropRectNormalized: {
        x: cropRect.x / imageWidth,
        y: cropRect.y / imageHeight,
        width: cropRect.width / imageWidth,
        height: cropRect.height / imageHeight,
      },
      imageWidth,
      imageHeight,
      displayWidth: cropRect.width,
      displayHeight: cropRect.height,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function removeParticleEmbeddedImageMetadata(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const next = { ...(metadata || {}) };
  delete next.relatedGraphEmbeddedImage;
  delete next.embeddedImage;
  delete next.relatedGraphEmbeddedImageOriginal;
  delete next.relatedGraphEmbeddedImageGenerated;
  delete next.relatedGraphEmbeddedImageActive;
  delete next.relatedGraphEmbeddedImageActiveModel;
  delete next.relatedGraphGeometryCandidates;
  delete next.relatedGraphEmbedImageKind;
  delete next.relatedGraphEmbedImageClassifier;
  return next;
}

export type EmbeddedImageActiveVariant = 'original' | 'generated';

function asEmbeddedRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** 构建全图裁剪（生成图整图作为嵌入图）。 */
export function buildFullFrameEmbeddedImage(input: {
  sourceAssetId?: string;
  sourceImageUrl: string;
  imageWidth: number;
  imageHeight: number;
}): {
  sourceAssetId?: string;
  sourceImageUrl: string;
  cropRect: ImageCropRect;
  imageWidth: number;
  imageHeight: number;
} {
  const imageWidth = Math.max(1, Math.round(input.imageWidth));
  const imageHeight = Math.max(1, Math.round(input.imageHeight));
  return {
    sourceAssetId: input.sourceAssetId,
    sourceImageUrl: input.sourceImageUrl,
    cropRect: { x: 0, y: 0, width: imageWidth, height: imageHeight },
    imageWidth,
    imageHeight,
  };
}

/**
 * 将「生成图」或「原始嵌入裁剪」设为当前正式嵌入图。
 * - original：优先恢复 relatedGraphEmbeddedImageOriginal
 * - generated：使用传入的 generated 全图记录，并备份当前原裁剪
 */
export function selectParticleEmbeddedImageVariant(
  metadata: Record<string, unknown> | null | undefined,
  variant: EmbeddedImageActiveVariant,
  generated?: {
    sourceAssetId?: string;
    sourceImageUrl: string;
    imageWidth: number;
    imageHeight: number;
  } | null,
): Record<string, unknown> {
  const base = { ...(metadata || {}) };
  const current = asEmbeddedRecord(base.relatedGraphEmbeddedImage);
  const originalBackup = asEmbeddedRecord(base.relatedGraphEmbeddedImageOriginal) || current;

  if (variant === 'original') {
    if (!originalBackup) {
      throw new Error('没有可恢复的原始嵌入图');
    }
    const next = patchParticleEmbeddedImageMetadata(base, {
      sourceAssetId: typeof originalBackup.sourceAssetId === 'string'
        ? originalBackup.sourceAssetId
        : undefined,
      sourceImageUrl: String(originalBackup.sourceImageUrl || ''),
      cropRect: {
        x: Number((originalBackup.cropRect as ImageCropRect | undefined)?.x) || 0,
        y: Number((originalBackup.cropRect as ImageCropRect | undefined)?.y) || 0,
        width: Number((originalBackup.cropRect as ImageCropRect | undefined)?.width) || 1,
        height: Number((originalBackup.cropRect as ImageCropRect | undefined)?.height) || 1,
      },
      imageWidth: Number(originalBackup.imageWidth) || 1,
      imageHeight: Number(originalBackup.imageHeight) || 1,
    });
    return {
      ...next,
      relatedGraphEmbeddedImageOriginal: originalBackup,
      relatedGraphEmbeddedImageActive: 'original',
    };
  }

  if (!generated?.sourceImageUrl) {
    throw new Error('缺少生成图信息');
  }
  if (originalBackup) {
    base.relatedGraphEmbeddedImageOriginal = originalBackup;
  }
  const full = buildFullFrameEmbeddedImage(generated);
  const next = patchParticleEmbeddedImageMetadata(base, full);
  const generatedRecord = asEmbeddedRecord(next.relatedGraphEmbeddedImage);
  return {
    ...next,
    relatedGraphEmbeddedImageGenerated: generatedRecord,
    relatedGraphEmbeddedImageActive: 'generated',
  };
}

export function readParticleEmbeddedImageActive(
  metadata: Record<string, unknown> | null | undefined,
): EmbeddedImageActiveVariant {
  const active = String(metadata?.relatedGraphEmbeddedImageActive || '').trim();
  return active === 'generated' ? 'generated' : 'original';
}

export function readParticleEmbeddedImageActiveModel(
  metadata: Record<string, unknown> | null | undefined,
): string {
  return String(metadata?.relatedGraphEmbeddedImageActiveModel || '').trim();
}

/** 走马灯「原始」槽：优先备份原裁切，避免正式图切到生成图后原始槽被覆盖。 */
export function readParticleEmbeddedImageOriginal(
  metadata: Record<string, unknown> | null | undefined,
): EmbeddedImageData | null {
  if (!metadata) return null;
  const original = asEmbeddedRecord(metadata.relatedGraphEmbeddedImageOriginal);
  if (original) return readEmbeddedImageRecord({ relatedGraphEmbeddedImage: original });
  if (readParticleEmbeddedImageActive(metadata) === 'original') {
    return readEmbeddedImageRecord(metadata);
  }
  return null;
}

export type PersistedGeometryCandidate = {
  id: string;
  label: string;
  model: string;
  provider: string;
  pngUrl: string | null;
  width?: number;
  height?: number;
  artifactUrl?: string | null;
  costUsd: number | null;
  costCny: number | null;
  costEstimated: boolean;
  elapsedMs: number;
  error?: string | null;
  assetId?: string | null;
  ossId?: string | null;
  persistedAt?: string | null;
};

/** 落库/缺省顺序；走马灯展示顺序由 geometryVendorKinds 按 GT/NGT 决定 */
const GEOMETRY_CANDIDATE_ORDER = [
  'dajiao-qwen-agent',
  'dajiao',
  'gpt-image-2',
  'qwen-image-3.0',
  'qwen-image-3.0-pro',
] as const;

function emptyGeometryCandidate(id: string): PersistedGeometryCandidate {
  const labels: Record<string, { label: string; model: string; provider: string }> = {
    dajiao: { label: 'DJG', model: 'dinogeo-1-pro', provider: 'dajiao' },
    'dajiao-qwen-agent': { label: 'DQA', model: 'qwen3.7-plus', provider: 'qwen+dajiao-mcp' },
    'gpt-image-2': { label: 'OI2', model: 'gpt-image-2', provider: 'image2' },
    'qwen-image-3.0': { label: 'QI3', model: 'qwen-image-3.0', provider: 'dashscope' },
    'qwen-image-3.0-pro': { label: 'QI3P', model: 'qwen-image-3.0-pro', provider: 'dashscope' },
  };
  const meta = labels[id] || { label: id, model: id, provider: '' };
  return {
    id,
    label: meta.label,
    model: meta.model,
    provider: meta.provider,
    pngUrl: null,
    costUsd: null,
    costCny: null,
    costEstimated: true,
    elapsedMs: 0,
    error: null,
    assetId: null,
    ossId: null,
    persistedAt: null,
  };
}

/** 始终返回全部候选槽（大角 / Qwen Agent / GPT Image 2 / Qwen Image），缺省为空槽。 */
export function readParticleGeometryCandidates(
  metadata: Record<string, unknown> | null | undefined,
): PersistedGeometryCandidate[] {
  const raw = metadata?.relatedGraphGeometryCandidates;
  const map = new Map<string, PersistedGeometryCandidate>();
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const record = asEmbeddedRecord(item);
      if (!record) continue;
      const id = String(record.id || '').trim();
      if (!GEOMETRY_CANDIDATE_ORDER.includes(id as typeof GEOMETRY_CANDIDATE_ORDER[number])) continue;
      map.set(id, {
        ...emptyGeometryCandidate(id),
        id,
        label: String(record.label || id),
        model: String(record.model || ''),
        provider: String(record.provider || ''),
        pngUrl: typeof record.pngUrl === 'string' && record.pngUrl.trim() ? record.pngUrl.trim() : null,
        width: Number(record.width) || undefined,
        height: Number(record.height) || undefined,
        artifactUrl: typeof record.artifactUrl === 'string' ? record.artifactUrl : null,
        costUsd: Number.isFinite(Number(record.costUsd)) ? Number(record.costUsd) : null,
        costCny: Number.isFinite(Number(record.costCny)) ? Number(record.costCny) : null,
        costEstimated: Boolean(record.costEstimated),
        elapsedMs: Number(record.elapsedMs) || 0,
        error: typeof record.error === 'string' ? record.error : null,
        assetId: typeof record.assetId === 'string' ? record.assetId : null,
        ossId: typeof record.ossId === 'string' ? record.ossId : null,
        persistedAt: typeof record.persistedAt === 'string' ? record.persistedAt : null,
      });
    }
  }
  return GEOMETRY_CANDIDATE_ORDER.map((id) => map.get(id) || emptyGeometryCandidate(id));
}

export async function resolveParticleEmbedSource(particle: KnowledgeParticle): Promise<ParticleEmbedSource | null> {
  const metadata = (particle.metadata ?? null) as Record<string, unknown> | null;
  const embedded = readEmbeddedImageRecord(metadata);
  const embeddedAssetId = readParticleSourceAssetId(metadata);

  if (embedded?.sourceImageUrl) {
    let ossId: string | undefined;
    if (embeddedAssetId) {
      try {
        const asset = await assetService.getAsset(embeddedAssetId);
        ossId = asset.oss_id;
        return {
          sourceAssetId: embeddedAssetId,
          sourceImageUrl: embedded.sourceImageUrl || asset.url,
          ossId,
        };
      } catch {
        return {
          sourceAssetId: embeddedAssetId,
          sourceImageUrl: embedded.sourceImageUrl,
        };
      }
    }
    return {
      sourceAssetId: embeddedAssetId,
      sourceImageUrl: embedded.sourceImageUrl,
    };
  }

  if (embeddedAssetId) {
    try {
      const asset = await assetService.getAsset(embeddedAssetId);
      if (asset.url) {
        return { sourceAssetId: asset.id, sourceImageUrl: asset.url, ossId: asset.oss_id };
      }
    } catch {
      // ignore
    }
  }

  try {
    const bundle = await kpWorkspaceService.getParticleWorkspaceGraph(particle.id);
    const assetNode = bundle.nodes.find((node) => node.kind === 'asset');
    const assetId = assetNode?.assetId || bundle.root.assetId;
    const previewUrl = assetNode?.imageUrl || bundle.root.surface?.previewUrl;
    if (assetId) {
      try {
        const asset = await assetService.getAsset(assetId);
        return {
          sourceAssetId: asset.id,
          sourceImageUrl: asset.url || previewUrl || '',
          ossId: asset.oss_id,
        };
      } catch {
        if (previewUrl) {
          return { sourceAssetId: assetId, sourceImageUrl: previewUrl };
        }
      }
    }
  } catch {
    // ignore
  }

  return null;
}

export function readParticleEmbeddedImage(metadata: Record<string, unknown> | null | undefined): EmbeddedImageData | null {
  return readEmbeddedImageRecord(metadata);
}
